import { isNil } from 'lodash-es'
import { NegotiationDetail, ValidationIssue } from 'api'
import { createInvalidValueValidationIssue, OrderTermHelper } from 'model'
import { Locale, Translation } from 'locale'

import { CurvePriceUpload } from './parseCurveSpreadsheet'
import { IncomingSpreadsheetParseResult } from 'view/shared/incomingUpload'
import { getNegotiatedValue } from 'view/routes/requests/shared/utils'
import { isEqual } from 'date-fns'
import { isNotNil } from 'utils/predicate'

export enum PendingCurveFields {
  COLLECTION_ID = 'COLLECTION_ID',
  START_DATE = 'START_DATE',
  END_DATE = 'END_DATE',
  TERM = 'TERM',
  RATE = 'RATE',
  HAIRCUT = 'HAIRCUT',
  HAIRCUT_TYPE = 'HAIRCUT_TYPE'
}

export const CUSTOM_TERM_PATTERN = /^custom/i
export const CUSTOM_OE_TERM_PATTERN = /custom.*O$/i

export const validateCurveParseResult = (
  parseResult: IncomingSpreadsheetParseResult<CurvePriceUpload>,
  negotiaitonDetailGetters: {
    getIncomingNegotiationDetailsByCollectionId: (collectionId: string) => NegotiationDetail[]
  }
): CurvePriceUpload[] => {
  const { getIncomingNegotiationDetailsByCollectionId } = negotiaitonDetailGetters
  const { priceUploads } = parseResult

  if (priceUploads.length === 0) {
    return []
  }

  return priceUploads
    .map(priceUpload => {
      const dataIssues: ValidationIssue[] = []
      let negotiationMatches: NegotiationDetail[] = []
      // validate collection id has been set
      if (isNil(priceUpload.collectionId)) {
        dataIssues.push(
          createInvalidValueValidationIssue(PendingCurveFields.COLLECTION_ID, Locale.translate(Translation.SC_MISSING_COLLECTION_ID))
        )
      }

      // validate term has been set
      if (isNil(priceUpload.term)) {
        dataIssues.push(createInvalidValueValidationIssue(PendingCurveFields.TERM, Locale.translate(Translation.SC_MISSING_TERM)))
      }

      if (!isNil(priceUpload.collectionId) && !isNil(priceUpload.term)) {
        const negotiationDetails = getIncomingNegotiationDetailsByCollectionId(priceUpload.collectionId)
        if (priceUpload.term.match(CUSTOM_TERM_PATTERN)) {
          const requireEndDate = !priceUpload.term.match(CUSTOM_OE_TERM_PATTERN)
          // custom term so lookup by dates
          if (isNil(priceUpload.startDate)) {
            dataIssues.push(
              createInvalidValueValidationIssue(
                PendingCurveFields.START_DATE,
                Locale.translate(Translation.SC_CUSTOM_TERM, { detail: Locale.translate(Translation.SC_MISSING_START_DATE) })
              )
            )
          }

          if (requireEndDate && isNil(priceUpload.endDate)) {
            dataIssues.push(
              createInvalidValueValidationIssue(
                PendingCurveFields.END_DATE,
                Locale.translate(Translation.SC_CUSTOM_TERM, { detail: Locale.translate(Translation.SC_MISSING_END_DATE) })
              )
            )
          }

          // lookup by startDate and endDate
          negotiationMatches.push(
            ...negotiationDetails.filter(n => {
              const startDate = getNegotiatedValue(n.negotiation.startDate)
              const endDate = getNegotiatedValue(n.negotiation.endDate)
              return (
                isEqual(new Date(startDate), priceUpload.startDate!) &&
                (isNotNil(endDate) ? isEqual(new Date(endDate), priceUpload.endDate!) : priceUpload.endDate === null)
              )
            })
          )
        } else {
          // lookup by collection id + term
          negotiationMatches.push(
            ...negotiationDetails.filter(
              n => OrderTermHelper.getName(getNegotiatedValue(n.negotiation.term)).toLowerCase() === priceUpload.term?.toLowerCase()
            )
          )
        }
      }

      if (negotiationMatches.length === 0) {
        dataIssues.push(
          createInvalidValueValidationIssue(
            PendingCurveFields.COLLECTION_ID,
            Locale.translate(Translation.SC_NO_MATCHES, {
              startDate: priceUpload.startDate,
              endDate: priceUpload.endDate,
              collectionId: priceUpload.collectionId,
              term: priceUpload.term
            })
          )
        )
      }

      if (negotiationMatches.length > 1) {
        dataIssues.push(
          createInvalidValueValidationIssue(
            PendingCurveFields.COLLECTION_ID,
            Locale.translate(Translation.SC_MULTIPLE_MATCHES, {
              startDate: priceUpload.startDate,
              endDate: priceUpload.endDate,
              collectionId: priceUpload.collectionId,
              term: priceUpload.term
            })
          )
        )
      }

      return {
        ...{ ...priceUpload, ...(negotiationMatches.length === 1 && { rfqId: negotiationMatches[0].negotiation.parentOrderId }) },
        dataIssues
      }
    })
    .reduce((validatedUploads, priceUpload, _index, _priceUploads) => {
      const isDuplicate = _priceUploads.filter(_ => isNotNil(_.rfqId) && _.rfqId === priceUpload.rfqId).length > 1
      return isDuplicate
        ? [
            ...validatedUploads,
            {
              ...priceUpload,
              dataIssues: [
                ...priceUpload.dataIssues,
                createInvalidValueValidationIssue(PendingCurveFields.COLLECTION_ID, Locale.translate(Translation.SC_DUPLICATE_UPLOAD)),
                createInvalidValueValidationIssue(PendingCurveFields.TERM, Locale.translate(Translation.SC_DUPLICATE_UPLOAD))
              ]
            }
          ]
        : [...validatedUploads, priceUpload]
    }, [])
}
